import { UNSETTLED_TRANSACTIONS_TREE_BLOTTER_ID } from '@src/containers/Portfolio/LpSettlement/tokens';
import type { AdvancedFilterModel, GridState } from 'ag-grid-community';
import type { AppConfigState } from '../AppConfigProvider.types';
import {
  LP_SETTLEMENT_GRID_LAYOUT_MIGRATION_VERSION,
  shouldRunLpSettlementGridLayoutMigration,
} from '../lpSettlementGridLayoutMigrationVersion';

export type LpSettlementGridLayoutMigrationConfig = {
  blotters3?: AppConfigState['blotters3'];
  lpSettlementGridLayoutMigrationVersion?: number;
};

const LEGACY_EVENT_ID_COL_ID = 'TradeID';
const EVENT_ID_COL_ID = 'EventID';

/** Legacy tree layouts stored Event ID column state under the TradeID colId. */
export function migrateLpSettlementTreeGridLayout(initialConfig: unknown) {
  const config = initialConfig as LpSettlementGridLayoutMigrationConfig;
  if (!shouldRunLpSettlementGridLayoutMigration(config)) {
    return;
  }

  const blotterState = config.blotters3?.[UNSETTLED_TRANSACTIONS_TREE_BLOTTER_ID];
  if (blotterState?.gridState != null) {
    blotterState.gridState = migrateLpSettlementTreeGridState(blotterState.gridState);
  }

  config.lpSettlementGridLayoutMigrationVersion = LP_SETTLEMENT_GRID_LAYOUT_MIGRATION_VERSION;
}

export function migrateLpSettlementTreeGridState(gridState: GridState): GridState {
  const migratedState: GridState = { ...gridState };

  if (gridState.columnOrder?.orderedColIds != null) {
    migratedState.columnOrder = {
      orderedColIds: remapColIdList(gridState.columnOrder.orderedColIds),
    };
  }

  if (gridState.columnVisibility?.hiddenColIds != null) {
    migratedState.columnVisibility = {
      hiddenColIds: remapColIdList(gridState.columnVisibility.hiddenColIds),
    };
  }

  if (gridState.columnPinning != null) {
    migratedState.columnPinning = remapColumnPinning(gridState.columnPinning);
  }

  if (gridState.columnSizing?.columnSizingModel != null) {
    migratedState.columnSizing = {
      columnSizingModel: remapColumnSizingModel(gridState.columnSizing.columnSizingModel),
    };
  }

  if (gridState.rowGroup?.groupColIds != null) {
    migratedState.rowGroup = {
      groupColIds: remapColIdList(gridState.rowGroup.groupColIds) ?? [],
    };
  }

  if (gridState.pivot?.pivotColIds != null) {
    migratedState.pivot = {
      ...gridState.pivot,
      pivotColIds: remapColIdList(gridState.pivot.pivotColIds) ?? [],
    };
  }

  if (gridState.sort?.sortModel != null) {
    migratedState.sort = {
      sortModel: remapSortModel(gridState.sort.sortModel),
    };
  }

  if (gridState.filter != null) {
    migratedState.filter = migrateFilterState(gridState.filter);
  }

  return migratedState;
}

function remapColId(colId: string): string {
  return colId === LEGACY_EVENT_ID_COL_ID ? EVENT_ID_COL_ID : colId;
}

function remapColIdList(colIds: string[]): string[] {
  const hasEventId = colIds.includes(EVENT_ID_COL_ID);
  const seenColIds = new Set<string>();
  const remappedColIds: string[] = [];

  for (const colId of colIds) {
    if (colId === LEGACY_EVENT_ID_COL_ID && hasEventId) {
      continue;
    }

    const remappedColId = remapColId(colId);
    if (seenColIds.has(remappedColId)) {
      continue;
    }
    seenColIds.add(remappedColId);
    remappedColIds.push(remappedColId);
  }

  return remappedColIds;
}

function remapColumnPinning(
  columnPinning: NonNullable<GridState['columnPinning']>
): NonNullable<GridState['columnPinning']> {
  const hasEventId = [...columnPinning.leftColIds, ...columnPinning.rightColIds].includes(EVENT_ID_COL_ID);
  const removeLegacyDuplicate = (colIds: string[]) =>
    hasEventId ? colIds.filter(colId => colId !== LEGACY_EVENT_ID_COL_ID) : colIds;

  return {
    leftColIds: remapColIdList(removeLegacyDuplicate(columnPinning.leftColIds)),
    rightColIds: remapColIdList(removeLegacyDuplicate(columnPinning.rightColIds)),
  };
}

function remapColumnSizingModel(
  columnSizingModel: NonNullable<GridState['columnSizing']>['columnSizingModel']
): NonNullable<GridState['columnSizing']>['columnSizingModel'] {
  const hasEventId = columnSizingModel.some(columnSize => columnSize.colId === EVENT_ID_COL_ID);
  const sizingByColId = new Map<string, (typeof columnSizingModel)[number]>();

  for (const columnSize of columnSizingModel) {
    if (columnSize.colId === LEGACY_EVENT_ID_COL_ID && hasEventId) {
      continue;
    }

    const remappedColId = remapColId(columnSize.colId);
    if (sizingByColId.has(remappedColId)) {
      continue;
    }
    sizingByColId.set(remappedColId, { ...columnSize, colId: remappedColId });
  }

  return [...sizingByColId.values()];
}

function remapSortModel(
  sortModel: NonNullable<GridState['sort']>['sortModel']
): NonNullable<GridState['sort']>['sortModel'] {
  const hasEventId = sortModel.some(sortItem => sortItem.colId === EVENT_ID_COL_ID);
  const seenColIds = new Set<string>();

  return sortModel
    .filter(sortItem => !(sortItem.colId === LEGACY_EVENT_ID_COL_ID && hasEventId))
    .map(sortItem => ({
      ...sortItem,
      colId: remapColId(sortItem.colId),
    }))
    .filter(sortItem => {
      if (seenColIds.has(sortItem.colId)) {
        return false;
      }
      seenColIds.add(sortItem.colId);
      return true;
    });
}

function migrateFilterState(filterState: NonNullable<GridState['filter']>): NonNullable<GridState['filter']> {
  return {
    ...filterState,
    filterModel: remapFilterModelRecord(filterState.filterModel),
    columnFilterState: remapFilterModelRecord(filterState.columnFilterState),
    advancedFilterModel: migrateOptionalAdvancedFilterModel(filterState.advancedFilterModel),
    selectableFilters: remapSelectableFilters(filterState.selectableFilters),
  };
}

function remapFilterModelRecord<T extends Record<string, unknown> | undefined>(filterModel: T): T {
  if (filterModel == null) {
    return filterModel;
  }

  const remappedFilterModel: Record<string, unknown> = {};
  const hasEventId = Object.prototype.hasOwnProperty.call(filterModel, EVENT_ID_COL_ID);
  for (const [colId, filterValue] of Object.entries(filterModel)) {
    if (colId === LEGACY_EVENT_ID_COL_ID && hasEventId) {
      continue;
    }

    const remappedColId = remapColId(colId);
    if (remappedColId in remappedFilterModel) {
      continue;
    }
    remappedFilterModel[remappedColId] = filterValue;
  }

  return remappedFilterModel as T;
}

function remapSelectableFilters(
  selectableFilters: NonNullable<GridState['filter']>['selectableFilters']
): NonNullable<GridState['filter']>['selectableFilters'] {
  if (selectableFilters == null) {
    return selectableFilters;
  }

  const remappedSelectableFilters: Record<string, number> = {};
  const hasEventId = Object.prototype.hasOwnProperty.call(selectableFilters, EVENT_ID_COL_ID);
  for (const [colId, filterIndex] of Object.entries(selectableFilters)) {
    if (colId === LEGACY_EVENT_ID_COL_ID && hasEventId) {
      continue;
    }

    const remappedColId = remapColId(colId);
    if (remappedColId in remappedSelectableFilters) {
      continue;
    }
    remappedSelectableFilters[remappedColId] = filterIndex;
  }

  return remappedSelectableFilters;
}

function migrateAdvancedFilterModel(advancedFilterModel: AdvancedFilterModel): AdvancedFilterModel {
  if (advancedFilterModel.filterType === 'join') {
    const hasEventId = advancedFilterModel.conditions.some(
      condition => condition.filterType !== 'join' && condition.colId === EVENT_ID_COL_ID
    );

    return {
      ...advancedFilterModel,
      conditions: advancedFilterModel.conditions
        .filter(
          condition => !(hasEventId && condition.filterType !== 'join' && condition.colId === LEGACY_EVENT_ID_COL_ID)
        )
        .map(migrateAdvancedFilterModel),
    };
  }

  return {
    ...advancedFilterModel,
    colId: remapColId(advancedFilterModel.colId),
  };
}

function migrateOptionalAdvancedFilterModel(
  advancedFilterModel: AdvancedFilterModel | undefined
): AdvancedFilterModel | undefined {
  if (advancedFilterModel == null) {
    return advancedFilterModel;
  }

  return migrateAdvancedFilterModel(advancedFilterModel);
}
